import { API } from "@swivl-mono/api-helper";
import { MemberAgentSettings } from "@swivl-mono/types";
import { BasicSelect, Checkbox } from "@swivl-mono/ui";
import { useEffect, useState } from "react";

import { SavedIndicator, useSavedIndicator } from "../../../../../Components/SavedIndicator/SavedIndicator";
import { Member } from "../../../../../Models/Member/Member.Current";
import MembersModel from "../../../../../Models/Member/Members.Model";
import { AlertsModel } from "../../../../../Models/Notifications/Alerts.Model";
import { AudioSettings } from "../../../../../Views/ContactCenterV3/Conversations/AudioSettings";

/** Per-browser flag: the explanatory first-visit version has been shown. */
export const AGENT_SETTINGS_SEEN_KEY = "ccv3.agentSettingsSeen";

/** Sentinel for "no personal default — use the org's primary number". */
const ORG_PRIMARY = "";

interface CallerIdOption {
  id: number;
  identifier: string;
  name: string;
}

export function AgentSettingsTab(): React.ReactNode {
  const member = Member.member;
  const [callerIds, setCallerIds] = useState<CallerIdOption[]>([]);
  const [settings, setSettings] = useState<MemberAgentSettings>(member?.agentSettings ?? {});
  const [isFirstVisit] = useState<boolean>(() => !localStorage.getItem(AGENT_SETTINGS_SEEN_KEY));
  const saved = useSavedIndicator();

  useEffect(() => {
    const orgId = Member.orgId;
    if (!orgId) return;
    let cancelled = false;
    const load = async (): Promise<void> => {
      const ids = await API.ContactCenterV3.getCallerIds(orgId);
      if (!cancelled) setCallerIds(ids.filter((c) => c.identifier));
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  // First visit: once the agent leaves the tab (switches away or closes the
  // modal — dismissing counts as done), remember it and point them back here.
  // Only counts when the tab was actually on screen for a moment — an
  // instant unmount (another modal replacing this one, dev double-mount)
  // must not burn the one first-visit showing.
  useEffect(() => {
    if (!isFirstVisit) return;
    const mountedAt = Date.now();
    return () => {
      if (Date.now() - mountedAt < 1000) return;
      localStorage.setItem(AGENT_SETTINGS_SEEN_KEY, "1");
      AlertsModel.successToast("You can adjust these anytime under Account Settings → Agent Settings.");
    };
  }, [isFirstVisit]);

  if (!member) {
    return null;
  }

  async function persist(next: MemberAgentSettings): Promise<void> {
    setSettings(next);
    if (member) {
      await MembersModel.updateMember({ id: member.id, agentSettings: next });
      saved.flash();
    }
  }

  return (
    <div className="flex flex-col gap-6">
      {isFirstVisit && (
        <div className="rounded-md border border-orange-200 bg-orange-50 px-4 py-3">
          <span className="body-sm-alt block text-orange-600">New: Agent Settings</span>
          <span className="body-sm text-grey-700">
            This is where your Contact Center calling setup lives. Pick the microphone and speaker used on calls, and choose which of your
            organization&apos;s numbers customers see when you call them.
          </span>
        </div>
      )}

      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <span className="heading-base-alt text-black">Audio Devices</span>
          <SavedIndicator visible={saved.show} />
        </div>
        <span className="body-sm text-grey-600">The microphone and speaker used for Contact Center calls. These are saved to this browser.</span>
        <div className="rounded-md border border-grey-200">
          <AudioSettings />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <span className="heading-base-alt text-black">Outbound Calling</span>
        <span className="body-sm text-grey-600">The number customers see when you call them.</span>
        <div className="w-[320px]">
          <BasicSelect
            name="default-outbound-number"
            hideClear={true}
            selectedValues={[settings.defaultOutboundNumber ?? ORG_PRIMARY]}
            options={[
              { label: "Org primary number (default)", value: ORG_PRIMARY },
              ...callerIds.map((c) => ({ label: `${c.name} (${c.identifier})`, value: c.identifier }))
            ]}
            onChange={(selected) => {
              const value = selected[0];
              if (value !== undefined) {
                void persist({ ...settings, defaultOutboundNumber: value === ORG_PRIMARY ? undefined : value });
              }
            }}
          />
        </div>
        <Checkbox
          label="Call from the customer's location number when they have one"
          checked={settings.callFromCustomerLocation ?? false}
          onCheckedChange={(checked) => void persist({ ...settings, callFromCustomerLocation: checked === true })}
          classNames={{ label: "body-sm text-grey-700" }}
        />
        <span className="body-sm text-grey-500">
          When this is on and the customer belongs to a location, calls dial from that location&apos;s number; otherwise your default above is used.
        </span>
      </div>
    </div>
  );
}
